import * as React from 'react';
import * as styles from './ContentOrganizerCreatePanel.css';
import { IADropDown } from '../../dropDown/DropDown';
import { IATextField } from '../../textField/TextField';
import { IAPeopleSearch } from '../../peopleSearch/PeopleSearch';
import { ContentOrganizerStore } from '../stores';
import { inject, observer } from 'mobx-react';
import { LocalizationService } from "./../../../services/LocalizationService";
import { IUser } from "./../../../interfaces/IUser";
import ContentOrganizerService from "./../../../services/ContentOrganizerService";
import { IASiteListRadioDropDown } from './../../siteListRadioDropDown/SiteListRadioDropDown';
import NavigatorService from '../../../services/NavigatorService';
import { IInstance } from '../../../interfaces/IInstance';
import { IOption } from '../../../interfaces/IOption';
import { Helper } from '../../../Helper';
import { IHubSite } from '../../../interfaces/IHubSite';
import { ISharePointSiteInformation } from '../../../interfaces/IContentOrganizerCreate';
import { IAToggle } from '../../toggle/Toggle';


const TimeZone = require('./TimeZones.json');

export interface IContentOrganizerCreatePanelProps {
  contentOrganizerStore?: ContentOrganizerStore;
  isNewSiteCollection?: boolean;
  onSelectedStep: (step) => void;
}

export interface IContentOrganizerCreatePanelState {
  instanceOptions: any[];
  hubSiteOptions: any[];
  selectedHubSiteOption?: IOption;
  selectedNavigatorOption?: IOption;
}


@inject('contentOrganizerStore')
@observer
export class ContentOrganizerCreatePanel extends React.Component<IContentOrganizerCreatePanelProps, IContentOrganizerCreatePanelState> {

  private localizationService = new LocalizationService();
  private timeout = undefined;

  constructor(props: IContentOrganizerCreatePanelProps) {
    super(props);
    this.state = {
      instanceOptions: [],
      hubSiteOptions: [],
      selectedHubSiteOption: null,
      selectedNavigatorOption: null,
    };
  }

  public componentDidMount() {
    this.getAllNavigators();
    this.getAllHubSites();
  }

  private getAllHubSites() {
    ContentOrganizerService.getHubSites(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.token, this.props.contentOrganizerStore.tenant)
      .then((hubSites: IHubSite[]) => {
        this.props.contentOrganizerStore.hubSiteList = hubSites;
        this.props.contentOrganizerStore.selectedHubSite ?? hubSites[0];
        let hubSiteOptions = this.state.hubSiteOptions;
        const placeholder: IOption = {
          key: "placeholder_" + Helper.getRandomStringKey(),
          value: this.localizationService.strings.ContentOrganizer_PlaceholderNone
        };
        hubSiteOptions.push(placeholder);
        hubSites.forEach(hubSite => {
          let hubSiteOption: IOption = {
            key: hubSite.id.toString(),
            value: hubSite.title
          }
          hubSiteOptions.push(hubSiteOption);
        });
        this.setState({ hubSiteOptions });
      });
  }



  private getAllNavigators() {
    NavigatorService.getInstances(this.props.contentOrganizerStore.token, `${this.props.contentOrganizerStore.tenant}.sharepoint.com`, this.props.contentOrganizerStore.environment, "IntraActive-NavigatorAdmin").then((instances: IInstance[]) => {
      this.props.contentOrganizerStore.navigatorInstances = instances;
      this.props.contentOrganizerStore.selectedNavigatorInstance = instances[0];
      let instanceOptions = this.state.instanceOptions;
      const placeholder: IOption = {
        key: "placeholder_" + Helper.getRandomStringKey(),
        value: this.localizationService.strings.ContentOrganizer_PlaceholderNone
      };
      instanceOptions.push(placeholder);
      instances.forEach(instance => {
        let instanceOption: IOption = {
          key: instance?.id.toString(),
          value: instance.title
        };
        instanceOptions.push(instanceOption);
      });
      this.setState({ instanceOptions });
    });
  }

  private getAndApplySelectedSiteInformation(siteName: string): void {
    const SupportedLanguages = [
      { key: "da-DK", value: this.localizationService.strings.ContentOrganizer_Danish },
      { key: "de-DE", value: this.localizationService.strings.ContentOrganizer_German },
      { key: "en-US", value: this.localizationService.strings.ContentOrganizer_English },
      { key: "pt-BR", value: this.localizationService.strings.ContentOrganizer_Portuguese },
      { key: "sv-SE", value: this.localizationService.strings.ContentOrganizer_Swedish }
    ];
    ContentOrganizerService.getSiteInformation(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.token, this.props.contentOrganizerStore.tenant, siteName)
      .then((siteInformation: ISharePointSiteInformation) => {
        // Set the hubsite association (if exists)
        if (siteInformation.associatedHubSiteName) {
          const selectedHubSite: IHubSite = this.props.contentOrganizerStore.hubSiteList.find(x => x.id == siteInformation.associatedHubSiteName);
          const selectedHubSiteOption: IOption = this.state.hubSiteOptions.find(x => x.value == siteInformation.associatedHubSiteName);
          this.props.contentOrganizerStore.useHubSite = selectedHubSite !== undefined ? true : false;
          this.props.contentOrganizerStore.selectedHubSite = selectedHubSite;
          this.setState({ selectedHubSiteOption });
        }
        // Set the navigator instance (if exists)
        if (siteInformation.navigatorInstanceId) {
          const selectedInstance: IInstance = this.props.contentOrganizerStore.navigatorInstances.find(x => x.id == siteInformation.navigatorInstanceId);
          const selectedNavigatorOption: IOption = this.state.instanceOptions.find(x => x.key == siteInformation.navigatorInstanceId.toString());
          this.props.contentOrganizerStore.useNavigator = selectedInstance !== undefined ? true : false;
          this.props.contentOrganizerStore.selectedNavigatorInstance = selectedInstance;
          this.setState({ selectedNavigatorOption });
        }
        // Set the site language
        if (siteInformation.language && SupportedLanguages.some(x => x.key === siteInformation.language)) {
          this.props.contentOrganizerStore.selectedLanguage = siteInformation.language;
        } else {
          this.props.contentOrganizerStore.selectedLanguage = "en-US";
        }
        // Set the fact if the site is a content organizer instance
        if (siteInformation.isContentOrganizerInstance) {
          this.props.contentOrganizerStore.isExistingContentOrganizerInstance = siteInformation.isContentOrganizerInstance;
        }
      });
  }

  private removeSelectedSiteInformation(): void {
    // Remove the hubsite association
    this.props.contentOrganizerStore.useHubSite = false;
    this.props.contentOrganizerStore.selectedHubSite = undefined;
    this.setState({ selectedHubSiteOption: null });
    // Remove the navigator instance
    this.props.contentOrganizerStore.useNavigator = false;
    this.props.contentOrganizerStore.selectedNavigatorInstance = undefined;
    this.setState({ selectedNavigatorOption: null });
    // Remove (reset) the language
    this.props.contentOrganizerStore.selectedLanguage = "en-US";
  }

  public render(): JSX.Element {
    const TemplateOptions = [
      { key: "handbook", value: this.localizationService.strings.ContentOrganizer_Handbook },
      { key: "basic", value: this.localizationService.strings.ContentOrganizer_BasicTemplate },
      { key: "noTemplate", value: this.localizationService.strings.ContentOrganizer_NoTemplate },
    ];
    let panel = (
      <div className={styles.ICreate_panel}>
        <div className={styles.ICreate_TitleContainer}>
          <div className={"IA_FontSize_24"}>
            {this.localizationService.strings.ContentOrganizer_CreateTitle}
          </div>
          <div>
            <p className={"IA_FontSize_14"}>{this.localizationService.strings.ContentOrganizer_CreateDescription}</p>
          </div>
        </div>
        <div className={styles.ICreate_BodyContainer}>
          <IADropDown
            options={TemplateOptions}
            label={this.localizationService.strings.ContentOrganizer_SelectTemplate}
            onChange={(option) => {
              this.props.contentOrganizerStore.selectedTemplate = option;
            }}
            selectedOption={this.props.contentOrganizerStore.selectedTemplate}
            style={{ width: "100%" }}
          />

          <IATextField
            label={this.localizationService.strings.ContentOrganizer_Title}
            text={this.props.contentOrganizerStore.contentOrganizerTitle}
            onChange={(title) => {
              this.props.contentOrganizerStore.contentOrganizerTitle = title;
              this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
            }}
            style={{ width: "100%" }}
            required={true}
          />

          <IATextField
            label={this.localizationService.strings.ContentOrganizer_SiteCollection}
            text={this.props.contentOrganizerStore.siteCollectionAddress}
            required={true}
            onChange={(siteName) => {
              if (this.timeout) {
                clearTimeout(this.timeout);
                this.props.contentOrganizerStore.siteCollectionAddress = siteName;
                this.props.contentOrganizerStore.siteCollectionAddressNameAvailable = undefined;
                this.props.contentOrganizerStore.siteCollectionAddressNameHasSpecialCharacters = undefined;
              }
              this.timeout = setTimeout(() => {
                // Search after no typing for x seconds
                this.props.contentOrganizerStore.siteCollectionAddress = siteName;
                if (siteName) {
                  if (/^((?!_)[\w-])+$/.test(siteName) === false) {
                    this.props.contentOrganizerStore.siteCollectionAddressNameHasSpecialCharacters = true;
                    this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
                  } else {
                    this.props.contentOrganizerStore.siteCollectionAddressNameHasSpecialCharacters = false;
                    ContentOrganizerService.isAddressAvailable(this.props.contentOrganizerStore.environment, this.props.contentOrganizerStore.token, this.props.contentOrganizerStore.tenant, siteName).then((isAvailable) => {
                      if (isAvailable) {
                        this.props.contentOrganizerStore.siteCollectionAddressNameAvailable = true;
                        this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
                      } else {
                        this.props.contentOrganizerStore.siteCollectionAddressNameAvailable = false;
                        this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
                      }
                    });
                  }
                } else {
                  this.props.contentOrganizerStore.siteCollectionAddress = undefined;
                  this.props.contentOrganizerStore.siteCollectionAddressNameAvailable = undefined;
                  this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
                }
              }, 500);
            }}
            style={{ width: "100%" }}
          />
          {this.props.contentOrganizerStore.siteCollectionAddress &&
            <p>{`https://${this.props.contentOrganizerStore.tenant}.sharepoint.com/sites/${this.props.contentOrganizerStore.siteCollectionAddress}`}</p>}

          {this.props.contentOrganizerStore.siteCollectionAddress && this.props.contentOrganizerStore.siteCollectionAddressNameAvailable &&
            this.props.contentOrganizerStore.siteCollectionAddressNameHasSpecialCharacters === false &&
            <div style={{ color: "#23C519" }}>{this.localizationService.strings.ContentOrganizer_Available}</div>}

          {this.props.contentOrganizerStore.siteCollectionAddress && this.props.contentOrganizerStore.siteCollectionAddressNameAvailable === false &&
            this.props.contentOrganizerStore.siteCollectionAddressNameHasSpecialCharacters === false &&
            <div style={{ color: "#c50f1f" }}>{this.localizationService.strings.ContentOrganizer_Unavailable}</div>}

          {this.props.contentOrganizerStore.siteCollectionAddress && this.props.contentOrganizerStore.siteCollectionAddressNameHasSpecialCharacters &&
            <div style={{ color: "#c50f1f" }}>{this.localizationService.strings.ContentOrganizer_HasSpecialCharacters}</div>}

          <IATextField
            label={this.localizationService.strings.ContentOrganizer_Description}
            text={this.props.contentOrganizerStore.contentOrganizerDescription}
            onChange={(text) => {
              this.props.contentOrganizerStore.contentOrganizerDescription = text;
            }}
            style={{ width: "100%", marginBottom: "15px" }}
          />

          <IADropDown
            options={[
              { key: "da-DK", value: this.localizationService.strings.ContentOrganizer_Danish },
              { key: "de-DE", value: this.localizationService.strings.ContentOrganizer_German },
              { key: "en-US", value: this.localizationService.strings.ContentOrganizer_English },
              { key: "pt-BR", value: this.localizationService.strings.ContentOrganizer_Portuguese },
              { key: "sv-SE", value: this.localizationService.strings.ContentOrganizer_Swedish }
            ]}
            label={this.localizationService.strings.ContentOrganizer_SelectLanguage}
            onChange={(option) => {
              this.props.contentOrganizerStore.selectedLanguage = option;
            }}
            selectedOption={this.props.contentOrganizerStore.selectedLanguage}
            style={{ width: "100%", marginBottom: "15px" }}
          />

          <div>
            <div style={{ marginBottom: "7px" }}>{this.localizationService.strings.ContentOrganizer_SiteOwner} <span style={{ color: "red" }}>*</span></div>
            <IAPeopleSearch
              token={this.props.contentOrganizerStore.token}
              tenant={this.props.contentOrganizerStore.tenant}
              environment={this.props.contentOrganizerStore.environment}
              multiSelection={true}
              selectedValues={this.props.contentOrganizerStore.siteOwners}
              onSelect={(list: IUser[]) => {
                this.props.contentOrganizerStore.siteOwners = list;
                this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
              }}
              style={{ width: "100%" }}
              showGroups={true}
            />
          </div>
          <IADropDown
            options={TimeZone}
            label={this.localizationService.strings.ContentOrganizer_TimeZone}
            onChange={(time) => {
              this.props.contentOrganizerStore.selectedTimeZone = time;
            }}
            selectedOption={this.props.contentOrganizerStore.selectedTimeZone}
            style={{ width: "100%" }}
          />
          <IADropDown
            options={this.state.hubSiteOptions}
            label={this.localizationService.strings.ContentOrganizer_SelectHubSite}
            onChange={(hubSite) => {
              const selectedHubSite: IHubSite = this.props.contentOrganizerStore.hubSiteList.find(x => x.id == hubSite);
              this.props.contentOrganizerStore.useHubSite = selectedHubSite !== undefined ? true : false;
              this.props.contentOrganizerStore.selectedHubSite = selectedHubSite;
            }}
            selectedOption={this.state.hubSiteOptions[0]}
            style={{ width: "100%" }}
          />
          <IADropDown
            options={this.state.instanceOptions}
            label={this.localizationService.strings.ContentOrganizer_SelectNavigator}
            onChange={(navigatorInstance) => {
              const selectedInstance: IInstance = this.props.contentOrganizerStore.navigatorInstances.find(x => x.id == navigatorInstance);
              this.props.contentOrganizerStore.useNavigator = selectedInstance !== undefined ? true : false;
              this.props.contentOrganizerStore.selectedNavigatorInstance = selectedInstance;
            }}
            selectedOption={this.state.instanceOptions[0]}
            style={{ width: "100%" }}
          />
          <IAToggle
            style={{
              display: "inline-block",
              marginRight: "10px",
              width: "200px",
              marginTop: "10px",
            }}
            label={this.localizationService.strings.ContentOrganizer_NavigatorHideHubMenu ?? "Hide Hub Menu"}
            checked={this.props.contentOrganizerStore.hideHubMenu}
            color={this.props.contentOrganizerStore.highlightColor}
            onChange={() => { this.props.contentOrganizerStore.hideHubMenu = !this.props.contentOrganizerStore.hideHubMenu }}
          />
          <IAToggle
            style={{
              display: "inline-block",
              marginRight: "10px",
              width: "200px"
            }}
            label={this.localizationService.strings.ContentOrganizer_NavigatorHideLocalMenu ?? "Hide Local Menu"}
            checked={this.props.contentOrganizerStore.hideLocalMenu}
            color={this.props.contentOrganizerStore.highlightColor}
            onChange={() => { this.props.contentOrganizerStore.hideLocalMenu = !this.props.contentOrganizerStore.hideLocalMenu }}
          />
        </div>
        <div className={styles.ICreate_BodyContainerRight}>
          <div className={styles.ICreate_Img}>
            <img src={"https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/wizard6.svg"} />
          </div>
          <div className={styles.ICreate_templateDescription}>
            {this.props.contentOrganizerStore.selectedTemplate === "handbook" &&
              <div>
                <p className={"IA_FontSize_20"}>{this.localizationService.strings.ContentOrganizer_Handbook}</p>
                <p className={"IA_FontSize_14"} dangerouslySetInnerHTML={{ __html: this.localizationService.strings.ContentOrganizer_TemplateDescriptionHandbook }} ></p>
              </div>
            }
            {this.props.contentOrganizerStore.selectedTemplate === "basic" &&
              <div>
                <p className={"IA_FontSize_20"}>{this.localizationService.strings.ContentOrganizer_BasicTemplate}</p>
                <p className={"IA_FontSize_14"} dangerouslySetInnerHTML={{ __html: this.localizationService.strings.ContentOrganizer_TemplateDescriptionBasicTemplate }} ></p>
              </div>
            }
            {this.props.contentOrganizerStore.selectedTemplate === "noTemplate" &&
              <div>
                <p className={"IA_FontSize_20"}>{this.localizationService.strings.ContentOrganizer_NoTemplate}</p>
                <p className={"IA_FontSize_14"} dangerouslySetInnerHTML={{ __html: this.localizationService.strings.ContentOrganizer_TemplateDescriptionNoTemplate }} ></p>
              </div>
            }
          </div>
        </div>
      </div>);

    let panelAdd = (
      <div className={styles.ICreate_panel}>
        <div className={styles.ICreate_TitleContainer}>
          <div className={"IA_FontSize_24"}>
            {this.localizationService.strings.ContentOrganizer_OptionsCreateSelect}
          </div>
          <div>
            <p className={"IA_FontSize_14"}>{this.localizationService.strings.ContentOrganizer_OptionsCreateSelectDescription}</p>
          </div>
        </div>
        <div className={styles.ICreate_BodyContainer}>
          <IADropDown
            options={TemplateOptions}
            label={this.localizationService.strings.ContentOrganizer_SelectTemplate}
            onChange={(option) => {
              this.props.contentOrganizerStore.selectedTemplate = option;
            }}
            selectedOption={this.props.contentOrganizerStore.selectedTemplate}
            style={{ width: "100%" }}
          />

          <IASiteListRadioDropDown
            tenant={this.props.contentOrganizerStore.tenant}
            environment={this.props.contentOrganizerStore.environment}
            token={this.props.contentOrganizerStore.token}
            label={this.localizationService.strings.ContentOrganizer_SelectSiteCollection}
            placeholder={this.localizationService.strings.ContentOrganizer_SiteSelectionPlaceHolder}
            singleOption={true}
            styles={{
              marginTop: "10px"
            }}
            selectedValue={this.props.contentOrganizerStore.selectedSiteItem}
            themeColor={"rgb(35, 135, 155)"}
            onSelectOptions={(options) => {
              let selectedItem;
              options.forEach(option => {
                if (option.selected) {
                  selectedItem = option;
                }
              });
              if (selectedItem) {
                const tokens = selectedItem.item.path.split("/");
                let selectedOption = tokens[tokens.length - 1];

                this.props.contentOrganizerStore.selectedSiteItem = selectedItem;
                this.props.contentOrganizerStore.contentOrganizerTitle = selectedItem.item.title;
                this.props.contentOrganizerStore.siteCollectionAddress = selectedOption;
                this.getAndApplySelectedSiteInformation(selectedItem.item.path.split('/').pop());
              } else {
                this.props.contentOrganizerStore.selectedSiteItem = undefined;
                this.props.contentOrganizerStore.siteCollectionAddress = undefined;
                this.props.contentOrganizerStore.contentOrganizerTitle = undefined
                this.removeSelectedSiteInformation();
              }
              this.props.onSelectedStep(this.props.contentOrganizerStore.selectedStep);
            }}
          />
          <IADropDown
            options={this.state.hubSiteOptions}
            label={this.localizationService.strings.ContentOrganizer_SelectHubSite}
            onChange={(hubSite) => {
              const selectedHubSite: IHubSite = this.props.contentOrganizerStore.hubSiteList.find(x => x.id == hubSite);
              this.props.contentOrganizerStore.useHubSite = selectedHubSite !== undefined ? true : false;
              this.props.contentOrganizerStore.selectedHubSite = selectedHubSite;
            }}
            selectedOption={this.state.selectedHubSiteOption?.key ?? this.state.hubSiteOptions[0]}
            style={{ width: "100%" }}
          />
          <IADropDown
            options={this.state.instanceOptions}
            label={this.localizationService.strings.ContentOrganizer_SelectNavigator}
            onChange={(navigatorInstance) => {
              const selectedInstance: IInstance = this.props.contentOrganizerStore.navigatorInstances.find(x => x.id == navigatorInstance);
              this.props.contentOrganizerStore.useNavigator = selectedInstance !== undefined ? true : false;
              this.props.contentOrganizerStore.selectedNavigatorInstance = selectedInstance;
            }}
            selectedOption={this.state.selectedNavigatorOption?.key ?? this.state.instanceOptions[0]}
            style={{ width: "100%", marginTop: 10 }}
          />
          <IAToggle
            style={{
              display: "inline-block",
              marginRight: "10px",
              width: "200px",
              marginTop: "10px",
            }}
            label={this.localizationService.strings.ContentOrganizer_NavigatorHideHubMenu ?? "Hide Hub Menu"}
            checked={this.props.contentOrganizerStore.hideHubMenu}
            color={this.props.contentOrganizerStore.highlightColor}
            onChange={() => { this.props.contentOrganizerStore.hideHubMenu = !this.props.contentOrganizerStore.hideHubMenu }}
          />
          <IAToggle
            style={{
              display: "inline-block",
              marginRight: "10px",
              width: "200px"
            }}
            label={this.localizationService.strings.ContentOrganizer_NavigatorHideLocalMenu ?? "Hide Local Menu"}
            checked={this.props.contentOrganizerStore.hideLocalMenu}
            color={this.props.contentOrganizerStore.highlightColor}
            onChange={() => { this.props.contentOrganizerStore.hideLocalMenu = !this.props.contentOrganizerStore.hideLocalMenu }}
          />
        </div>
        <div className={styles.ICreate_BodyContainerRight}>
          <div className={styles.ICreate_Img}>
            <img src={"https://intraactivestorage.blob.core.windows.net/cdn/SDK%20Images/wizard6.svg"} />
          </div>
          <div className={styles.ICreate_templateDescription}>
            {this.props.contentOrganizerStore.selectedTemplate === "handbook" &&
              <div>
                <p className={"IA_FontSize_20"}>{this.localizationService.strings.ContentOrganizer_Handbook}</p>
                <p className={"IA_FontSize_14"} dangerouslySetInnerHTML={{ __html: this.localizationService.strings.ContentOrganizer_TemplateDescriptionHandbook }} ></p>
              </div>
            }
            {this.props.contentOrganizerStore.selectedTemplate === "basic" &&
              <div>
                <p className={"IA_FontSize_20"}>{this.localizationService.strings.ContentOrganizer_BasicTemplate}</p>
                <p className={"IA_FontSize_14"} dangerouslySetInnerHTML={{ __html: this.localizationService.strings.ContentOrganizer_TemplateDescriptionBasicTemplate }} ></p>
              </div>
            }
            {this.props.contentOrganizerStore.selectedTemplate === "noTemplate" &&
              <div>
                <p className={"IA_FontSize_20"}>{this.localizationService.strings.ContentOrganizer_NoTemplate}</p>
                <p className={"IA_FontSize_14"} dangerouslySetInnerHTML={{ __html: this.localizationService.strings.ContentOrganizer_TemplateDescriptionNoTemplate }} ></p>
              </div>
            }
          </div>
        </div>
      </div>);

    if (this.props.contentOrganizerStore.newSiteCollection) {
      return panel;
    }

    return panelAdd;
  }

}